import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import classNames from 'classnames'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { PrimaryButton } from '@components/UI-Components/PrimaryButton'
import { LinearProgress, makeStyles } from '@material-ui/core'
import { SecondaryButton } from '@components/UI-Components/SecondaryButton'
import Typography from '@components/std-components/Typography'
import { PopupSize } from '@components/UI-Components/StandardPopup'

interface TaggingDialogProps {
  popupTitle?: string
  children?: any
  bulkOpSaveProgress?: number
  disableSave?: boolean
  isBulkOpSaveActive?: boolean
  bulkOpSaveError?: boolean
  maxWidth?: PopupSize
  isBulkOperation?: boolean
  cancelHandler?: () => void
  saveHandler?: () => void
  cancelButtonLabel?: string
}

const useStyles = makeStyles(theme => ({
  container: {
    overflow: 'hidden',
    display: 'flex',
    justifyContent: 'flex-start',
    flexDirection: 'column'
  },
  popupRoot: {
    overflow: 'auto'
  },
  borderTop: {
    borderTop: '1px solid var(--lightgrey)'
  },
  actionBar: {
    display: 'flex',
    alignItems: 'center',
    padding: '20px 25px 25px'
  },
  cancelBtn: {
    width: 120,
    height: 34,
    marginRight: 10
  },
  applyBtn: {
    width: 120,
    height: 34,
    borderRadius: 0,
    backgroundColor: '#444444'
  },
  savingButton: {
    width: 120,
    height: 34,
    borderRadius: 0,
    backgroundColor: '#CCCCCC',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    margin: 0,
    position: 'relative'
  },
  progressBar: {
    backgroundColor: '#CCCCCC',
    width: '100%',
    height: 5,
    marginRight: 10,
    '& .MuiLinearProgress-colorPrimary': {
      backgroundColor: '#CCCCCC'
    },
    '& .MuiLinearProgress-barColorPrimary': {
      backgroundColor: '#3B6DE3CC'
    }
  },
  errorInstructionTextContainer: {
    //
  },
  savingInstruction: {
    width: '50%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center'
  },
  instructionText: {
    width: '90%'
  },
  instructionProgress: {
    width: '60%',
    display: 'flex',
    alignItems: 'center'
  },
  buttonsContainer: {
    width: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end'
  },
  savingText: {
    fontWeight: 600,
    textTransform: 'none',
    color: '#3B6DE3 !important'
  },
  disabledPopup: {
    pointerEvents: 'none',
    opacity: 0.3
  },
  buttonFont: {
    fontSize: '12px',
    fontWeight: 600,
    lineHeight: '1.25rem',
    letterSpacing: '0.023rem',
    textTransform: 'uppercase'
  }
}))

export const TaggingDialog: React.FunctionComponent<TaggingDialogProps> = props => {
  const {
    children,
    cancelHandler,
    saveHandler,
    maxWidth,
    bulkOpSaveProgress,
    disableSave,
    isBulkOperation,
    isBulkOpSaveActive,
    bulkOpSaveError
  } = props
  const { t } = useTranslation()
  const classes = useStyles()
  return (
    <Dialog open={true} fullWidth={true} maxWidth={maxWidth || PopupSize.small}>
      <div className={classes.popupRoot}>
        <div className={classes.container}>
          <DialogContent className={isBulkOpSaveActive ? classes.disabledPopup : ''}>
            {children}
          </DialogContent>
          <DialogActions
            className={classNames(
              classes.actionBar,
              !isBulkOperation ? classes.borderTop : null
            )}
          >
            <div className={classes.savingInstruction}>
              {isBulkOpSaveActive && !bulkOpSaveError ? (
                <>
                  <div className={classes.instructionText}>
                    <Typography variant={'caption2'} classes={classes.savingText}>
                      {t(
                        'bb.bulkProductOps.leaveWindowOpen',
                        'Leave your browser window open until done '
                      )}
                    </Typography>
                  </div>
                  <div className={classes.instructionProgress}>
                    <LinearProgress
                      className={classes.progressBar}
                      color='primary'
                      variant='determinate'
                      value={bulkOpSaveProgress}
                    />
                    <Typography
                      variant='buttonlabelTemplate'
                      classes={classes.buttonFont}
                    >
                      {bulkOpSaveProgress}%
                    </Typography>
                  </div>
                </>
              ) : null}
            </div>
            <div className={classes.buttonsContainer}>
              <SecondaryButton
                variant='text'
                onClick={cancelHandler}
                className={classes.cancelBtn}
                disabled={isBulkOpSaveActive}
              >
                {t('bb.common.label.cancel', 'Cancel')}
              </SecondaryButton>
              <div>
                <PrimaryButton
                  className={classes.applyBtn}
                  onClick={saveHandler}
                  disabled={!!disableSave || isBulkOpSaveActive}
                >
                  {t('bb.common.label.save', 'Save')}
                </PrimaryButton>
              </div>
            </div>
          </DialogActions>
        </div>
      </div>
    </Dialog>
  )
}
